import { isItemSurface, skeletonFor } from '@/app/skeleton-routes';

/**
 * The dock's Back on compact, its first slot (docs/MOBILE.md §2.4): an app
 * installed to an iOS home screen runs without browser chrome, so a page
 * reached by drilldown needs its own way back. `shellBackFor` says where that
 * Back walks when popping history would leave the app.
 *
 * The rule:
 * - null on a dock home (`/work`, `/activity`, `/boards`), where Back only
 *   pops or dismisses and is disabled while it can do neither; on an item
 *   surface (its own dock row carries Back), on `/admin` and below (its dock
 *   has Home), and on the auth and invite routes, where the shell draws no
 *   Back;
 * - a detail page walks to its index (`/groups/<id>` to `/groups`,
 *   `/account/contacts` to `/account`);
 * - a browsing page of the Boards lane (a board, the map, the tag explorer,
 *   and the indexes of the Browse footer) walks to `/boards`;
 * - every other page walks to `/work`.
 *
 * Pure, so the shell and its tests ask the same function. A trailing slash is
 * ignored.
 */
export function shellBackFor(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (HOMES.has(path)) return null;
  if (isItemSurface(skeletonFor(path)) || path === '/items' || path.startsWith('/items/')) {
    return null;
  }
  if (path === '/admin' || path.startsWith('/admin/')) return null;
  if (STANDALONE.test(path)) return null;

  for (const index of DETAIL_INDEXES) {
    if (path.startsWith(`${index}/`)) return index;
  }
  if (VIEWS_LANE.has(path) || /^\/boards\/[^/]+$/.test(path)) return '/boards';
  return '/work';
}

/** The dock's homes, and the root that redirects to Work. */
const HOMES: ReadonlySet<string> = new Set(['/', '/work', '/activity', '/boards']);

/** The auth and invite pages: the shell does not wrap them for an anonymous
 *  request, and they portal no title (the same set as `NO_TITLE` in
 *  `loading-chrome.ts`). */
const STANDALONE = /^\/(signin|signup|reset-password|verify-email|invite|join|connect)(\/|$)/;

/** Indexes that are pages: a page below one walks to it. */
const DETAIL_INDEXES: readonly string[] = [
  '/groups',
  '/views',
  '/flows',
  '/plans',
  '/ceremonies',
  '/activity',
  '/account',
];

/** The Boards lane's browsing pages besides the boards: the base
 *  map, the tag explorer, and the indexes the Browse footer links. */
const VIEWS_LANE: ReadonlySet<string> = new Set([
  '/groups',
  '/views',
  '/flows',
  '/flow-phases',
  '/plans',
  '/ceremonies',
  '/tags',
  '/map',
]);
